import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ModalController, ToastController } from '@ionic/angular';
import { UserProfileService } from '../../services/user-profile/user-profile.service';
import {
  SmartdineImageRestControllerService,
  RestaurantEntity,
  ProfileModel,
  RestaurantRestControllerService,
  RestaurantBranchRestControllerService,
  GeoLocation,
  SmartDineResponseRestaurantModel,
  CurrencyModel,
  RestaurantModelPrefTimeZone,
} from 'src/app/swagger';
import { LoaderService } from '../../services/loader/loader.service';
import { RestaurantAdminModel } from 'src/app/swagger/model/restaurantAdminModel';
import { AddressComponent } from '../maps/address/address.component';
import { RestaurantRegistrationForAdminService } from 'src/app/swagger/api/restaurantRegistrationForAdmin.service';
import { SmartDineResponseRestaurantForAdmin } from 'src/app/swagger/model/smartDineResponseRestaurantForAdmin';

import {
  COUNTRY_CODE,
  COUNTRY_LIST,
  CURRENCY_CODES,
  CURRENCY_LIST,
  INDIAN_STATES,
  TIME_ZONES,
  USA_STATES,
} from './data-list.helper';
import { UtilityService } from '../../services/utilities/utilities.service';

@Component({
  selector: 'app-restaurant-upsertion',
  templateUrl: './restaurant-upsertion.component.html',
  styleUrls: ['./restaurant-upsertion.component.scss'],
  standalone: false,
})
export class RestaurantUpsertionComponent implements OnInit {
  public static readonly id = 'resto-upsertion-modal';

  @Input() public restaurantId: string | null = null;
  @Input() restaurantTitle: string = 'New Restaurant';

  public restaurantEntity: RestaurantEntity | null = null;
  public restaurantAdminEntity: RestaurantAdminModel = {};
  public userProfile: ProfileModel | null = null;
  public restaurantForm: FormGroup;
  public countries: string[] = COUNTRY_LIST;
  public timeZones: RestaurantModelPrefTimeZone[] = [];
  public indianStates: string[] = INDIAN_STATES;
  public usaStates: string[] = USA_STATES;
  public countryCodes: string[] = COUNTRY_CODE;
  public currencyList: CurrencyModel[] = CURRENCY_LIST;
  public currencyCodes: string[] = CURRENCY_CODES;
  public selectedCountry: string = 'India';
  public selectedTimeZone: string = 'Asia/Kolkata';
  public subscriptionTypeId: string = 'basicRestaurantSubscriptionEntityId';

  get restaurantStatus() {
    return Object.keys(RestaurantAdminModel.RestaurantStatusEnum);
  }

  get orderTypes() {
    return Object.keys(RestaurantAdminModel.AllowedOrderTypesEnum);
  }

  get paymentPreferences() {
    return Object.keys(RestaurantAdminModel.AllowedPaymentPreferencesEnum);
  }

  constructor(
    private modalController: ModalController,
    private userProfileService: UserProfileService,
    public formBuilder: FormBuilder,
    private restaurantControllerService: RestaurantRestControllerService,
    private branchControllerService: RestaurantBranchRestControllerService,
    private toastController: ToastController,
    private loaderService: LoaderService,
    private smartdineImageRestControllerService: SmartdineImageRestControllerService,
    private registrationServiceForAdmin: RestaurantRegistrationForAdminService,
    private utilService: UtilityService
  ) {
    this.createForm();
  }

  private createForm() {
    this.restaurantForm = this.formBuilder.group({
      restaurantName: new FormControl('', [Validators.required]),
      restaurantLogoUrl: new FormControl('', []),
      restaurantDescription: new FormControl('', [Validators.required]),
      restaurantCode: new FormControl('', [Validators.required]),
      fssai: new FormControl('', []),
      gstId: new FormControl('', []),
      restaurantStatus: new FormControl('', [Validators.required]),
      prefTimeZone: new FormControl('', [Validators.required]),
      maxNoOfTables: new FormControl(1, [Validators.required]),
      maxNoOfRestaurantBranches: new FormControl(1, [Validators.required]),
      prefCurrencyCode: new FormControl('', [Validators.required]),

      restaurantBranchCode: new FormControl('', [Validators.required]),
      restaurantBranchName: new FormControl('', [Validators.required]),
      restaurantBranchFssai: new FormControl('', []),
      restaurantBranchGstId: new FormControl('', []),
      restaurantBranchEmailId: new FormControl('', [Validators.required]),
      restaurantBranchContactNo: new FormControl('', [Validators.required]),
      restaurantBranchAddress: new FormControl('', [Validators.required]),
      restaurantBranchWebsite: new FormControl('', []),
      restaurantBranchCity: new FormControl('', [Validators.required]),
      restaurantCountry: new FormControl('India', [Validators.required]),
      restaurantBranchState: new FormControl('', [Validators.required]),
      restaurantBranchCountryCode: new FormControl('+91', [
        Validators.required,
      ]),
      allowedPaymentPreferences: new FormControl('', [Validators.required]),
      allowedOrderTypes: new FormControl('', [Validators.required]),
      restaurantAdminUserId: new FormControl('', [Validators.required]),
      restaurantSubscriptionEntityId: new FormControl(this.subscriptionTypeId, [
        Validators.required,
      ]),
      location: new FormControl(
        { latitude: 0, longitude: 0 } as GeoLocation,
        []
      ),
    });
  }

  public ngOnInit() {
    this.setUserProfile();
    this.updateTimeZones(this.selectedCountry);
    if (this.restaurantId) {
      this.loadRestaurant(this.restaurantId);
    }
  }

  private setUserProfile() {
    this.userProfile = null;
    this.userProfileService.getUserProfile().subscribe({
      next: (userProfile: ProfileModel) => {
        this.userProfile = userProfile;
      },
    });
  }

  private loadRestaurant(restaurantId: string) {
    this.restaurantControllerService.getRestaurant(restaurantId).subscribe({
      next: (res) => {
        this.restaurantEntity = res.item;
        this.subscriptionTypeId = res?.item?.restaurantSubscriptionEntityId;

        this.patchRestaurantForm(this.restaurantEntity);

        // Load branch after restaurant
        this.loadFirstBranch(restaurantId);
      },
      error: (err) => {
        console.error('Unable to get Restaurant details!', err);
      },
    });
  }

  private loadFirstBranch(restaurantId: string) {
    this.branchControllerService
      .getRestaurantBranches1(restaurantId)
      .subscribe({
        next: (res) => {
          if (res?.items?.length > 0) {
            const branch = res.items[0]; // pick first branch
            this.patchBranchForm(branch);
          }
        },
        error: (err) => {
          console.error('Unable to get Branch details!', err);
        },
      });
  }

  private patchRestaurantForm(restaurant: any) {
    this.restaurantForm?.patchValue({
      restaurantId: restaurant?.restaurantId ?? '',
      restaurantCode: restaurant?.restaurantCode ?? '',
      restaurantName: restaurant?.name ?? '',
      restaurantDescription: restaurant?.description ?? '',
      restaurantLogoUrl: restaurant?.iconUrl ?? '',
      gstId: restaurant?.gstIN ?? '', // will be overwritten if branch has gstIN
      fssai: restaurant?.fssai ?? '', // will be overwritten if branch has fssai
      restaurantStatus: restaurant?.restaurantStatus ?? '',
      validUpto: restaurant?.validUpto ?? '',
      maxNoOfTables: restaurant?.maxNoOfTables ?? 0,
      maxNoOfRestaurantBranches: restaurant?.maxNoOfRestaurantBranches ?? 0,
      active: restaurant?.active ?? false,
      restaurantSubscriptionEntityId: this.subscriptionTypeId,
      prefCurrencyCode: restaurant?.prefCurrencyCode ?? null,
      prefTimeZone: restaurant?.prefTimeZone ?? null,
      prefCurrency: restaurant?.prefCurrency ?? null,
      prefTimeZoneId: restaurant?.prefTimeZoneId ?? null,
    });
  }
  
  private patchBranchForm(branch: any) {
    this.restaurantForm?.patchValue({
      restaurantBranchId: branch?.restaurantBranchId ?? '',
      restaurantBranchCode: branch?.restaurantBranchCode ?? '',
      restaurantBranchName: branch?.name ?? '',
      restaurantBranchFssai: branch?.fssai ?? this.restaurantForm.get('fssai')?.value, // fallback to restaurant
      restaurantBranchGstId: branch?.gstIN ?? this.restaurantForm.get('gstId')?.value,
      restaurantBranchEmailId: branch?.emailId ?? '',
      restaurantBranchContactNo: branch?.contactNumber ?? '',
      restaurantBranchAddress: branch?.address ?? '',
      restaurantBranchWebsite: branch?.website ?? '',
      restaurantBranchCity: branch?.city ?? '',
      restaurantBranchState: branch?.state ?? '',
      restaurantCountry: branch?.country ?? 'India',
      restaurantBranchCountryCode: branch?.countryCode ?? '+91',
      allowedPaymentPreferences: branch?.allowedPaymentPreferences,
      allowedOrderTypes: branch?.allowedOrderTypes,
      location: branch?.location ?? { latitude: 0, longitude: 0 },
    });
  }
  

  public onCloseClick() {
    this.modalController.dismiss('', '', RestaurantUpsertionComponent.id);
  }

  public onSaveClick() {
    
    if (!this.restaurantForm.valid) {
      this.utilService.showAlert(
        `Please fill all the required fields and try again.`,
        'Invalid Input',
        'invalid'
      );
      return;
    }

    this.restaurantForm.markAllAsTouched();
    if (this.restaurantId) {
      this.updateRestaurant();
    } else {
      this.registerRestaurant();
    }
  }

  private registerRestaurant() {
    let restaurantModel: RestaurantAdminModel =
      this.restaurantAdminEntity || {};
    Object.assign(restaurantModel, this.restaurantForm?.value);
    // restaurantModel = {
    //   ...restaurantModel,
    //   allowedPaymentPreferences: restaurantModel?.allowedPaymentPreferences,
    //   allowedOrderTypes: restaurantModel?.allowedOrderTypes?,
    // }

    restaurantModel['restaurantId'] =
      restaurantModel.restaurantName.replace(/\s+/g, '') + 'RestaurantId';
    restaurantModel['restaurantBranchId'] =
      restaurantModel.restaurantBranchName.replace(/\s+/g, '') + 'BranchId';

    
    this.registerNewRestaurant(restaurantModel);
  }

  private registerNewRestaurant(restaurantModel: RestaurantAdminModel) {
    
    this.registrationServiceForAdmin
      .createRestaurant(restaurantModel)
      .subscribe({
        next: (res: SmartDineResponseRestaurantForAdmin) => {
          let data = { refresh: 'true', type: 'created', restaurant: res.item };
          this.modalController.dismiss(
            data,
            '',
            RestaurantUpsertionComponent.id
          );
          this.utilService.showAlert(
            `Restaurant - ${res.item['name']}, Created Successfully.`,
            'Restaurant Registeration',
            'valid'
          );
        },
        error: (err) => {
          this.utilService.showAlert(
            `Something went wrong while registering the Restaurant - ${err.message}`,
            'Registeration Failed',
            'invalid'
          );
        },
      });
  }

  private updateRestaurant() {
    const formattedFormData: RestaurantEntity = this.mapFormToValidEntity(
      this.restaurantForm?.value
    );

    
    this.updatedRestaurant(formattedFormData);
  }

  private updatedRestaurant(restaurantModel: RestaurantEntity) {
    this.restaurantControllerService
      .updateRestaurant(restaurantModel, this.restaurantId)
      .subscribe({
        next: (res: SmartDineResponseRestaurantModel) => {
          let data = { refresh: 'true', type: 'updated', restaurant: res.item };
          this.modalController.dismiss(
            data,
            '',
            RestaurantUpsertionComponent.id
          );
          this.utilService.showAlert(
            `Restaurant - ${res.item['name']}, Updated Successfully.`,
            'Restaurant Updated',
            'valid'
          );
        },
        error: (err) => {
          this.utilService.showAlert(
            `Something went wrong while updating the Restaurant - ${err.message}`,
            'Registeration Failed',
            'invalid'
          );
        },
      });
  }

  private async showToast(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 2000,
    });
    toast.present();
  }

  /**
   * Method handler to be called on image file browsing/drop.
   */
  onImageSelection(event) {
    const files = event.target.files;
    if (files.length && this.restaurantEntity.restaurantId) {
      this.uploadImage(files[0]);
    }
  }

  /**
   * Upload Image.
   * @param file Image File.
   */
  uploadImage(imageFile: any) {
    const restaurantId = this.restaurantEntity.restaurantId; // TODO: Bug in API; Need fixing..
    const fileKey = this.restaurantEntity?.iconImage?.key;
    let uploadRequest = this.smartdineImageRestControllerService.uploadFileForm(
      imageFile,
      this.restaurantId
    );
    if (fileKey) {
      this.loaderService.showLoader();
      uploadRequest = this.smartdineImageRestControllerService.updateFileForm(
        fileKey,
        imageFile,
        this.restaurantId
      );
    }

    this.loaderService.showLoader();
    uploadRequest.subscribe(
      (res) => {
        this.restaurantEntity.iconImage = res.item;
        this.loaderService.hideLoader();
      },
      (err) => {
        this.utilService.showAlert(
          `Unable to upload the image - ${err.message}! Please try again.`,
          'Upload Failed',
          'invalid'
        );
        this.loaderService.hideLoader();
      }
    );
  }

  public onCountryChange(event: Event) {
    this.selectedCountry = event['detail']?.value;
    this.updateTimeZones(this.selectedCountry);

    if (event['detail'].value === 'India') {
      this.restaurantForm?.get('restaurantBranchState')?.setValue('');
    } else if (event['detail'].value === 'USA') {
      this.restaurantForm?.get('restaurantBranchState')?.setValue('');
    }
  }

  public showMap(locationFormControl: any) {
    this.showMapPopup(locationFormControl);
  }

  public async showMapPopup(locationControl: any) {
    const geolocation: GeoLocation = locationControl.value;
    const model = await this.modalController.create({
      component: AddressComponent,
      id: AddressComponent.id,
      componentProps: {
        location: geolocation,
      },
    });
    model.onDidDismiss().then((selLocation) => {
      locationControl.setValue({
        latitude: selLocation?.data?.location?.lat,
        longitude: selLocation?.data?.location?.lng,
      });
    });
    await model.present();
  }

  private mapFormToValidEntity(formValue: RestaurantAdminModel) {
    return {
      restaurantId: this.restaurantId,
      restaurantCode: formValue?.restaurantCode ?? '',
      name: formValue?.restaurantName ?? '',
      description: formValue?.restaurantDescription ?? '',
      iconUrl: formValue?.restaurantLogoUrl ?? '',
      gstIN: formValue?.gstId ?? '',
      fssai: formValue?.fssai ?? '',
      restaurantStatus: formValue?.restaurantStatus ?? 'PUBLISHED',
      maxNoOfTables: formValue?.maxNoOfTables ?? 0,
      maxNoOfRestaurantBranches: formValue?.maxNoOfRestaurantBranches ?? 0,
      restaurantSubscriptionEntityId:
        formValue?.restaurantSubscriptionEntityId ?? this.subscriptionTypeId,
      prefTimeZoneId: formValue?.prefTimeZone ?? null,
      prefTimeZone:
        this.timeZones?.find((time) => time.id === formValue?.prefTimeZoneId) ??
        null,
      prefCurrencyCode: formValue?.prefCurrencyCode ?? null,
      prefCurrency:
        this.currencyList.find(
          (currency) => currency.currencyCode === formValue?.prefCurrencyCode
        ) ?? null,
      allowedOrderTypes: formValue?.allowedOrderTypes,
      allowedPaymentPreferences: formValue?.allowedPaymentPreferences,
    };
  }

  private updateTimeZones(country: string) {
    if (country === 'India') {
      this.timeZones = TIME_ZONES.filter((zone) => zone.id.startsWith('Asia'));
      this.selectedTimeZone = this.timeZones.find((t) =>
        t.id.startsWith('Asia')
      ).id;
    } else if (country === 'USA') {
      this.timeZones = TIME_ZONES.filter((zone) => zone.id.startsWith('US'));
      this.selectedTimeZone = this.timeZones.find((t) =>
        t.id.startsWith('US')
      ).id;
    }
  }

}
